import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { nameofFactory, parseEnumStart, hasWriteAccess } from "../../utils/utils";
import { EditActions, editActions } from "../../actions/editActions";
import CheckboxField from "../../components/Editing/CheckboxField";
import Table from "../../components/Table/Table";
import { ioBoardTypeColumnsForBoardEditor } from "../../reducers/liftEnumerationsConfig";
import { getActionEnumOptions, getCpuOptions } from "../../utils/pdApiUtils";
import DropdownField from "../../components/Editing/DropdownField";
import arrow_right from "../../images/arrow-right.svg";
import trash from "../../images/trash.svg";
import MenuButton from "../../components/MenuButton/MenuButton";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { editedObjectIdSelector } from "../../reducers/editReducer";
import { pdApiActions } from "../../actions/pdApiActions";
type StateProps = {
  editedStandardBoard: Api.StandardBoard;
  storedStandardBoard: Api.StandardBoard;
  editedObjectId: string;
  pdApi: State.PdApi;
  ioConfig: State.IoConfig;

  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
  pdApiActions: typeof pdApiActions;
};

type OwnProps = {
  toggleConfirm: () => void;
};

const baseEnumFields = nameofFactory<Api.StandardBoard>();

export type EditStandardBoardProps = StateProps & DispatchProps & OwnProps;

export class EditStandardBoard extends React.Component<EditStandardBoardProps> {
  state = {
    nodeToBeDeleted: undefined
  };

  componentDidMount(): void {
    if (this.props.editedObjectId) {
      // The standard board to be edited does not contain the latest changes to related 
      // standard points/actions, let's fetch them first
      this.props.pdApiActions.fetchSingle("standardBoards", this.props.editedObjectId);
    }
  }

  componentDidUpdate(prevProps: Readonly<EditStandardBoardProps>): void {
    const { editedStandardBoard, ioConfig } = this.props;
    if (!_.isEqual(ioConfig.standardPoints, prevProps.ioConfig.standardPoints)) {
      // update the just fetched points/actions to the board
      const updatedStandardNodes: Api.StandardNode[] =
        editedStandardBoard.standardNodes?.map(
          node => ({
            ...node,
            standardPoints: ioConfig.standardPoints.filter(sp => sp.parentNodeId === node.id)
          })
        ) || [];
      this.props.editActions.setEdited({ ...editedStandardBoard, standardNodes: updatedStandardNodes });
    }
  }

  setNodeToBeDeleted(nodeId: string): void {
    this.setState({ nodeToBeDeleted: nodeId });
  }

  handleAddNodeClick = (): void => {
    this.props.editActions.addNode(this.props.editedStandardBoard.id);
  };
  handleNodeDeleteClick = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.props.editActions.deleteById("standardNodes", this.state.nodeToBeDeleted!);
    this.setState({ nodeToBeDeleted: undefined });
  };
  render(): JSX.Element {
    const {editedStandardBoard, storedStandardBoard, activeUser, toggleConfirm, pdApi } = this.props;
    const isNew = !storedStandardBoard;
    const cpuOptions = getCpuOptions(pdApi.ioCpuEnums);
    const networkOptions = getActionEnumOptions(pdApi.ioNetworks);
    const standardNodes = editedStandardBoard.standardNodes;

    const standardNodesEditor = standardNodes?.map(node => (
      <div key={node.id} className="flex-row-centered">
        <DropdownField
          id="cpu"
          value={node.cpu.enumValue}
          options={cpuOptions}
          search={true}
          disabled={isNew}
          onChange={index => {
            const cpu = pdApi.ioCpuEnums[index];
            const updatedNodes = standardNodes.map(n =>
              n.id === node.id ? { ...node, cpu: cpu } : n
            );
            this.props.editActions.setEdited({
              ...editedStandardBoard,
              standardNodes: updatedNodes
            });
          }}
        />
        <img src={arrow_right} alt="arrow" className="arrow" />
        <DropdownField
          id="network"
          value={node.network.enumValue}
          options={networkOptions}
          search={true}
          disabled={isNew}
          onChange={enumValue => {
            const network = pdApi.ioNetworks.find(nw => nw.enumValue == enumValue);
            const updatedNodes = standardNodes.map(n =>
              n.id === node.id ? { ...node, network } : n
            );
            this.props.editActions.setEdited({
              ...editedStandardBoard,
              standardNodes: updatedNodes
            });
          }}
        />
        <div className="editor-delete">
          {hasWriteAccess(activeUser, "Lift") && !isNew && (
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            <button className="basic" onClick={() => node.id && this.setNodeToBeDeleted(node.id)}>
              <img src={trash} alt="delete" />
            </button>
          )}
        </div>
      </div>
    ));
    // Build standardBoard table for selection
    const boardtypeRows = this.props.pdApi.ioBoardTypes;
    const selectedColumns: State.Column[] = ioBoardTypeColumnsForBoardEditor;
    return (
      <Editor
        itemName={"Standard Board"}
        originalObject={storedStandardBoard}
        editedObject={editedStandardBoard}
        hasWritePermission={hasWriteAccess(activeUser, "Lift")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("standardBoards" as Api.OptionsGrud)}
        create={() => this.props.editActions.add("standardBoards" as Api.OptionsGrud)}
        copy={() => this.props.editActions.startEditing({ ...editedStandardBoard, id: undefined }, true)}
        remove={{ callback: toggleConfirm }}
      >
        {isNew ? (
          <Table
            disablePagination={true}
            rows={boardtypeRows}
            columns={selectedColumns}
            isHidingTextActive={false}
            handleRowSelection={
               (boardType => this.props.editActions.setEdited({ ...editedStandardBoard, boardType: boardType }))
            }
            isRowSelected={row => row.boardTypeId === editedStandardBoard.boardType.boardTypeId}
            pageSize={10000}
            setHeight="600px"
          />
        ) : (
          <div className="flex-row space-between">
            <h3>{parseEnumStart(editedStandardBoard.boardType.enumName, 2)}</h3>
            <CheckboxField
              id="locked"
              label="locked"
              checked={editedStandardBoard.locked}
              onChange={value =>
                this.props.editActions.setEdited({ ...editedStandardBoard, locked: value })
              }
            />
          </div>
        )}
        <TextField
          id="revision"
          label="revision"
          value={editedStandardBoard.revision}
          isError={!this.isFieldValid(baseEnumFields("revision"))}
          disabled={!isNew}
          onChange={revision =>
            this.props.editActions.setEdited({ ...editedStandardBoard, revision: revision })
          }
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedStandardBoard.comment}
          isError={!this.isFieldValid(baseEnumFields("comment"))}
          onChange={comment =>
            this.props.editActions.setEdited({ ...editedStandardBoard, comment: comment })
          }
        />

        <div className="node-editor-wrapper">
          <h3>Nodes</h3>
          {standardNodes && standardNodesEditor}
          <br />
          {hasWriteAccess(activeUser, "Lift") && (
            <MenuButton
              type="Add"
              handleOnClick={() => this.handleAddNodeClick()}
              customText="ADD NODE"
              disabled={isNew}
            />
          )}
        </div>
        {this.state.nodeToBeDeleted && (
          <ConfirmationModal
            isOpen={!!this.state.nodeToBeDeleted}
            size="mini"
            header="Confirm delete"
            content={<span>Are you sure you wanna delete node?</span>}
            confirmText="Confirm"
            cancelText="Cancel"
            onConfirm={() => this.handleNodeDeleteClick()}
            onCancel={() => this.setState({ nodeToBeDeleted: undefined })}
          />
        )}
      </Editor>
    );
  }

  private isFieldValid = (field: string): boolean => {
    const { editedStandardBoard, storedStandardBoard, ioConfig } = this.props;
    switch (field) {
      case baseEnumFields("revision"): {
        const isNew = !storedStandardBoard;
        const reservedRevisionNames = ioConfig.standardBoards
          .filter(x => x.boardType.enumName === editedStandardBoard.boardType.enumName)
          .map(x => x.revision);
        const notEmpty = editedStandardBoard.revision.trim().length > 0;
        const unique = !reservedRevisionNames?.includes(editedStandardBoard.revision.trim());
        const unchangedEdit =
          !isNew && editedStandardBoard.revision === editedStandardBoard.revision;
        return reservedRevisionNames ? (notEmpty && unique) || unchangedEdit : true;
      }
      default:
        return true;
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedStandardBoard } = this.props;
    const temp = _.keys(editedStandardBoard);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedStandardBoard: state.editView.editedObject,
  storedStandardBoard: state.editView.storedObject,
  editedObjectId: editedObjectIdSelector(state.editView),
  pdApi: state.pdApi,
  ioConfig: state.ioConfig,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditStandardBoard);


